import { useState } from 'react';
import type { Matcher } from 'react-day-picker';
import * as Popover from '@radix-ui/react-popover';
import { Calendar as CalendarIcon } from '@ui/icons';
import { cn } from '@shared/lib';
import { Calendar } from './Calendar';

export interface DatePickerProps {
  value?: Date | undefined;
  onChange?: (date: Date | undefined) => void;
  placeholder?: string;
  /** Accessible name for the trigger (required). */
  'aria-label': string;
  disabled?: boolean;
  /** Disable specific dates on the calendar popup. */
  disabledDates?: Matcher | Matcher[] | undefined;
  className?: string;
  /** Optional custom display formatter for trigger label. */
  formatDisplayDate?: ((date: Date) => string) | undefined;
}

/**
 * Date input that opens a `Calendar` in a Radix Popover (Figma date field + picker).
 * **A11y:** labelled trigger; the popover traps focus and the calendar is keyboard-navigable.
 */
export function DatePicker({
  value,
  onChange,
  placeholder = 'Select date',
  disabled,
  disabledDates,
  className,
  formatDisplayDate,
  ...props
}: DatePickerProps) {
  const [open, setOpen] = useState(false);
  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <button
          type="button"
          aria-label={props['aria-label']}
          disabled={disabled}
          className={cn(
            'flex h-12 w-full items-center justify-between gap-2 rounded-lg border border-border-soft bg-surface-raised px-4 text-base',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            'disabled:cursor-not-allowed disabled:opacity-50',
            value ? 'text-fg' : 'text-fg/50',
            className,
          )}
        >
          <span>
            {value
              ? formatDisplayDate
                ? formatDisplayDate(value)
                : value.toLocaleDateString()
              : placeholder}
          </span>
          <CalendarIcon className="size-4 text-muted-soft" />
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align="start"
          sideOffset={6}
          className="z-50 rounded-lg border border-border bg-surface-raised p-3 shadow-overlay"
        >
          <Calendar
            mode="single"
            selected={value}
            disabled={disabledDates}
            onSelect={(date) => {
              onChange?.(date);
              if (date) setOpen(false);
            }}
          />
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
